import { AppFeaturesSystemName, Company } from '@apis/Customers/model';
import { inject, injectable } from 'tsyringe';
import { ICompanyContextToken } from '../Customers/CompanyContext';
import { UserFeatureAccessService } from '../Customers/UserFeatureAccessService';
import { CostForecastQueryDatasourceFactory } from '../Invoices/CostForecastService';
import { InvoiceApiService } from '../Invoices/InvoiceApiService';
import { InvoiceQueryDatasourceFactory } from '../Invoices/InvoiceQueryDatasourceFactory';
import { ResourceQueryDatasourceFactory } from '../Resources/ResourceQueryDatasourceFactory';
import { ResourceQueryService } from '../Resources/ResourceService';
import { IQueryDatasourceFactory, QueryDatasource } from './QueryDatasource';

@injectable()
export class QueryDatasourceCollection {
    public readonly datasourceFactories: IQueryDatasourceFactory[] = [];

    public constructor(
        @inject(InvoiceQueryDatasourceFactory) private readonly invoiceQueryDatasourceFactory: InvoiceQueryDatasourceFactory,
        @inject(InvoiceApiService) private readonly invoiceApi: InvoiceApiService,
        @inject(CostForecastQueryDatasourceFactory) private readonly costForecastQueryDatasourceFactory: CostForecastQueryDatasourceFactory,
        @inject(ResourceQueryDatasourceFactory) private readonly resourceQueryDatasourceFactory: ResourceQueryDatasourceFactory,
        @inject(ResourceQueryService) private readonly resourceQuerySvc: ResourceQueryService,
        @inject(ICompanyContextToken) private readonly company: Company,
        @inject(UserFeatureAccessService) private readonly accessSvc: UserFeatureAccessService
    ) {
        this.datasourceFactories.push(invoiceQueryDatasourceFactory, resourceQueryDatasourceFactory, costForecastQueryDatasourceFactory);
    }

    public getDatasourceMeta(datasourceName: string) {
        return this.datasourceFactories.find((f) => f.getMetadata().name === datasourceName)?.getMetadata();
    }

    public async getDatasource<TContext>(name: string, context: TContext): Promise<QueryDatasource | undefined>;
    public async getDatasource<TContext>(name: string, context: TContext, skipAccessCheck: true): Promise<QueryDatasource>;
    public async getDatasource<TContext>(name: string, context: TContext, skipAccessCheck: boolean = false) {
        if (!skipAccessCheck) {
            const meta = this.getDatasourceMeta(name);
            const accessLookup = await this.accessSvc.getAccessLookup(this.company);
            if (!meta || !accessLookup.checkAccessByName(meta?.feature)) {
                return undefined;
            }
        }
        switch (name) {
            case 'cur':
                return await this.invoiceQueryDatasourceFactory.getDatasource((query) => this.invoiceApi.query(query, context, false));
            case 'resources':
                return await this.resourceQueryDatasourceFactory.getDatasource((query) => this.resourceQuerySvc.query(query, this.company.Id ?? 0));
            case 'costforecast':
                return await this.costForecastQueryDatasourceFactory.getDatasource();
            default:
                throw new Error(`Unknown datasource: ${name}`);
        }
    }
}
